import React, { memo } from 'react';
import { useStyles } from 'uinix-ui';

import { ActionMode } from '../../types/ActionMode';
import { IconColor } from '../../types/IconColor';
import { IconType } from '../../types/IconType';
import { SharedButtonProps } from '../../types/SharedButtonProps';
import { TextColor } from '../../types/TextColor';
import { Count } from '../Count/Count';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { TruncateText } from '../Text/subcomponents/TruncateText';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import { PresetKey } from './types';
import { usePreset } from './usePreset';

interface Props extends SharedButtonProps {
  /** For setting aria-label */
  label?: string;
  preset?: PresetKey;
  PRIVATE_isPadded?: boolean;
}

export const Button = memo((props: Props) => {
  const styles = useStyles();

  const {
    count,
    disabled,
    icon: propIcon,
    iconPosition: propIconPosition,
    isFullWidth: initialIsFullWidth = false,
    isInline = false,
    isLoading = false,
    PRIVATE_isPadded = true,
    label,
    level,
    mode: propMode,
    preset,
    shouldTruncate = false,
    size: propSize,
    status: propStatus,
    text: propText,
    textVariant: propTextVariant,
    theme,
    tooltip,
    type = 'button',
    variant: propVariant,
    onClick,
    ...rest
  } = props;

  const presetResult = usePreset(preset);

  const initialIcon = presetResult.icon ?? propIcon;
  const iconPosition = presetResult.iconPosition ?? propIconPosition ?? 'right';
  const size = presetResult.size ?? propSize ?? 'm';
  const status = presetResult.status ?? propStatus;
  const text = presetResult.text ?? propText;
  const textVariant = presetResult.textVariant ?? propTextVariant;
  const initialVariant = presetResult.variant ?? propVariant ?? 'secondary';
  const mode = presetResult.mode ?? propMode;

  const isIconMode = mode === 'icon';
  const isAreaMode = mode === 'area';
  const isChipMode = mode === 'chip';
  const isRoundMode = mode === 'round';

  const variant = level ?? (isAreaMode ? 'ghost' : initialVariant); // always override the variant in specific modes
  const isFullWidth = isAreaMode || initialIsFullWidth;

  const isAction = variant === 'action';
  const isDarkTheme = theme === 'dark';
  const isDropdown = preset === 'dropdown';
  const isRightIcon = iconPosition === 'right';
  const isNoop = !Boolean(onClick) && type !== 'submit';

  const as = isNoop ? 'div' : 'button';
  const role = isNoop ? 'button' : undefined;
  const icon = isLoading ? 'loading' : initialIcon;
  const isSmallSize = size === 's' || size === 'xs';

  const { h, pl, pr, w } = resolveStyleProps({
    icon,
    isAction,
    isPadded: PRIVATE_isPadded,
    isFullWidth,
    isRightIcon,
    mode,
    size,
  });

  const buttonIcon = icon && (
    <Icon
      icon={icon}
      color={status ? (`status.${status}` as IconColor) : undefined}
    />
  );
  const chevronDown = isDropdown && <Icon icon="chevron-down" mr={-1} />;

  const buttonVariantStyle = isDarkTheme
    ? styles.button.dark[variant]
    : styles.button[variant];

  const darkThemeInteractionStyle =
    isDarkTheme && !isAction ? styles.interactionsInverse : undefined;

  const TextComponent = shouldTruncate ? TruncateText : Text;

  const borderRadius = isAreaMode
    ? 'unset'
    : isChipMode
      ? 'pill'
      : isRoundMode
        ? 'round'
        : undefined;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      event.preventDefault();
      event.stopPropagation();
      onClick(event);
    }
  };

  return (
    <Tooltip
      tooltip={tooltip}
      triggerStyle={isFullWidth ? { width: '100%' } : undefined}
    >
      <Layout
        {...rest}
        as={as}
        align="center"
        aria-disabled={isNoop ? disabled || isLoading : undefined}
        aria-label={label ?? (isIconMode ? text : undefined)}
        borderRadius={borderRadius}
        data-eds-styles-disable-interactable={isAction}
        disabled={isNoop ? undefined : disabled || isLoading}
        h={h}
        inline={isInline}
        justify={isAreaMode ? 'flex-start' : 'center'}
        maxW={shouldTruncate ? 'button.max-width' : undefined}
        pl={pl}
        pr={pr}
        py={0}
        role={role}
        spacing={1}
        styles={[
          styles.button.base,
          status ? styles.colors.status : null,
          status ? null : buttonVariantStyle,
          darkThemeInteractionStyle,
        ]}
        styleProps={{
          status,
          variant,
        }}
        w={w}
        type={type}
        onClick={handleClick}
      >
        {!isRightIcon && buttonIcon}
        {!isIconMode && !isRoundMode && (
          <>
            <TextComponent
              color={
                isChipMode && status
                  ? (`status.${status}` as TextColor)
                  : 'inherit'
              }
              variant={textVariant ?? (isSmallSize ? 'tiny' : 'body-medium')}
            >
              {text}
            </TextComponent>
            {(count || count === 0) && (
              <Count
                count={count}
                status={status ? status : variant === 'cta' ? 'cta' : undefined}
              />
            )}
          </>
        )}
        {isRightIcon && buttonIcon}
        {isDropdown && chevronDown}
      </Layout>
    </Tooltip>
  );
});

const resolveStyleProps = ({
  icon,
  isAction,
  isFullWidth,
  isPadded,
  isRightIcon,
  mode,
  size,
}: {
  icon?: IconType;
  isAction: boolean;
  isFullWidth: boolean;
  isPadded: boolean;
  isRightIcon: boolean;
  mode?: ActionMode;
  size: 'xs' | 's' | 'm' | 'l';
}) => {
  const h = isAction && mode !== 'icon' ? undefined : `height.${size}`;
  let w = isFullWidth ? '100%' : undefined;

  let px;
  switch (size) {
    case 'l': {
      px = [8, 8];
      break;
    }
    case 'xs': {
      px = [2, 2];
      break;
    }
    default: {
      px = [4, 4];
    }
  }

  switch (mode) {
    case 'area': {
      px = [0, 0];
      break;
    }
    case 'icon':
    case 'round': {
      px = [0, 0];
      w = h; // same size as height
      break;
    }
    default: {
      if (icon) {
        if (size === 's') {
          px = isRightIcon ? [3, 2] : [2, 3];
        } else {
          px = isRightIcon ? [4, 3] : [3, 4];
        }
      }
      break;
    }
  }

  const [pl, pr] = isPadded ? px : [0, 0];

  return { h, pl, pr, w };
};
